import { FileApi } from '@api';
import { EntryApi } from '@api/EntryApi';
import { EntryPrintFormApi } from '@api/EntryPrintFormApi';
import { translate } from '@chaika/i18n/helper';
import { entryPrintFormService } from '@patient/services/entry/entryPrintFormService';

import { store } from '@store';
import { ENTRY_FILE_DEFAULT_NAME } from '@store/types';

import { DateFormat, DateHelper } from '@utils/DateHelper';
import { isDefined } from '@utils/isDefined';
import { parseFileName, parseFilePath } from '@utils/parseFileName';
import { EntryFileCategory, FileEntityType, Locale } from 'chaika-enums';
import { EntryCreateForm, FileViewModel } from 'chaika-frontend-types/frontend-cms';
import type { EntryCdaCreationParams, FileCreationBody } from 'chaika-types';

export function buildEntryFileDownloadName(extension: string): string | undefined {
    const { entry, ehr, authUser } = store;
    const locale = entry.selectedLang ?? authUser.selectedLanguage ?? Locale.Ru;
    const fullName = ehr.getFullNameByLang(locale).trim();

    let filename: string | undefined;

    if (fullName) {
        const entryDate = DateHelper.format(entry.visitTime ?? entry.creationTime, DateFormat.DateWithDotSeparator);
        const suffix = extension ? `.${extension}` : '';
        filename = `${ehr.recordId} ${fullName} ${entryDate} ${entry.type.title}${suffix}`;
    }

    return filename;
}

export interface CreateEntryParams extends Omit<EntryCreateForm, 'organizationId'> {
    typeId?: string;
}

export enum EntryRelation {
    EntryType = 'entryType',
    RedesignEntryExpandedData = 'redesignEntryExpandedData',
    Versions = 'versions',
    Translations = 'translations',
    EntryTypeItemPresentation = 'entryTypeItemPresentation',
    EntryAudioRecord = 'entryAudioRecord',
}

export enum WellKnownAllowToAssistantEntryTypeIds {
    AttachedDocument = 'daa7b1b3-331c-4492-8697-67f63b1e4484',
}

interface UploadFileParams extends Omit<FileCreationBody, 'fileName'> {
    file: File;
    replace?: boolean;
    targetEntryId?: string;
}

export interface SignedPdf {
    file: File;
    signature: File;
}

class EntryFilesService {
    public async deleteFile(filePath: string, isFileLink?: boolean): Promise<void> {
        const { entry, app } = store;

        const fileNameQuery = isFileLink ? parseFilePath(filePath) : parseFileName(filePath, false);

        app.doOrShowError({
            action: async () => {
                await EntryApi.deleteFile(entry.id, fileNameQuery);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                isFileLink ? entry.deleteFileByPath(filePath) : entry.deleteFile(fileNameQuery);
            },
            errorMessage: translate('errorOnFileDeleting'),
            allowMultiAction: true,
        });
    }

    public async removeSingEntrtFile(entryId: string): Promise<void> {
        await EntryApi.deleteFile(entryId, ENTRY_FILE_DEFAULT_NAME).catch(() => {});
        store.entry.deleteFile(ENTRY_FILE_DEFAULT_NAME);
    }

    public async updateSingEntryFile(entryId: string): Promise<void> {
        const locale = store.entry.selectedLang ?? store.authUser.selectedLanguage ?? Locale.Ru;
        const pdfData = await entryPrintFormService.getSignedEntryPdf(entryId);
        const pdfFile = new File([pdfData], ENTRY_FILE_DEFAULT_NAME, { type: 'application/pdf' });

        await this.uploadFile({
            file: pdfFile,
            replace: true,
            targetEntryId: entryId,
            category: EntryFileCategory.PrintForm,
            details: { locale },
        });
    }

    public async signWithEds(entryId: string, pdf: SignedPdf, cda?: EntryCdaCreationParams): Promise<void> {
        const { entry } = store;

        await Promise.all([this.deleteEds(), EntryPrintFormApi.deleteEntryHtml(entryId)]);
        const replace = true;
        await Promise.all([
            this.uploadFile({ file: pdf.file, replace, targetEntryId: entryId, category: EntryFileCategory.PrintForm }),
            this.uploadFile({ file: pdf.signature, replace, targetEntryId: entryId }),
        ]);
        await EntryApi.signWithEds(entryId, {
            pdf: { filePath: pdf.file.name, signatureFilePath: pdf.signature.name },
            cda,
        });

        if (!!entry.id) {
            const [{ files }, eds] = await Promise.all([EntryApi.getEntry(entry.id), EntryApi.getEntryEDS(entry.id)]);

            this.setFiles(files);
            entry.setEDS(eds);
        }
    }

    public async loadEDS(): Promise<void> {
        const entry = store.entry;

        const eds = await EntryApi.getEntryEDS(entry.id);

        if (isDefined(eds)) {
            entry.setEDS(eds);
        }
    }

    public async addPacsLink(link: string): Promise<void> {
        const { app, entry, authUser } = store;

        app.doOrShowError({
            action: async () => {
                await FileApi.addPacsFile(entry.id, FileEntityType.Entry, link);

                entry.addFile({
                    path: link,
                    category: EntryFileCategory.Diagnostic,
                    authorId: authUser.id,
                    createdAt: new Date().toISOString(),
                });
            },
            errorMessage: translate('fileAlreadyExists'),
        });
    }

    private async deleteEds(): Promise<void> {
        const entry = store.entry;

        if (isDefined(entry.eds)) {
            await Promise.all([this.deleteFile(entry.eds.pdfFilePath), this.deleteFile(entry.eds.sgnFilePath)]);

            await EntryApi.deleteEds(entry.id);
            entry.setEDS();
        }
    }

    private async uploadFile({ file, replace = false, targetEntryId, ...params }: UploadFileParams): Promise<void> {
        const entry = store.entry;
        const entryId = targetEntryId ?? entry.id;

        if (replace) {
            await EntryApi.deleteFile(entryId, file.name).catch(() => {});
            entry.deleteFile(file.name);
        }

        const asset = await EntryApi.addFile(entryId, { fileName: file.name, ...params });

        for await (const _ of FileApi.uploadProcess(file, { fileName: asset.path })) {
            continue;
        }

        await EntryApi.setFileUploadedStatus(entryId, file.name);

        if (!!entry.id) {
            entry.addFile(asset);
        }
    }

    private setFiles(files: FileViewModel[]): void {
        store.entry.files = [];
        files.forEach(file => store.entry.addFile(file));
        store.entryPage.rightMenu.hasFiles = !!files.length;
    }
}

export const entryFilesService = new EntryFilesService();
